import type { JobOffer } from '@vv/common';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { paths, type JobOrigin, type SearchReturnState } from '../routes';
import { formatDate } from '../utils/format';
import { accentBackground, accentFor } from '../utils/pavilion-accent';

import { StandLogo } from './StandLogo';

export interface JobCardProps {
  job: JobOffer;
  /** Search results also show which exhibitor posted the job. */
  showCompany?: boolean;
  /** Passed on to the job page, whose back link then returns there. */
  from?: JobOrigin;
  /** Router state for the job page; see `SearchReturnState`. */
  state?: SearchReturnState;
}

export function JobCard({ job, showCompany = false, from, state }: JobCardProps): ReactElement {
  const { t } = useTranslation();
  // An offer belongs to at most one pavilion; its position drives the accent.
  const pavilion = job.pavilions[0];

  return (
    <Link
      to={paths.job(job.stand.slug, job.id, from)}
      state={state}
      className="card relative block overflow-hidden py-4 pr-4 pl-5 transition-colors hover:bg-ink-800"
    >
      {pavilion !== undefined && (
        <span
          aria-hidden="true"
          className={`absolute top-4 bottom-4 left-0 w-1 rounded-r ${accentBackground(
            accentFor(pavilion.position),
          )}`}
        />
      )}

      <h3 className="font-bold text-fog-100">{job.title}</h3>
      {job.location !== null && (
        <p className="mt-1 text-sm text-fog-500">
          {t('job.location')} {job.location}
        </p>
      )}
      {job.deadline !== null && (
        <p className="text-sm text-fog-500">
          {t('job.deadline')} {formatDate(job.deadline)}
        </p>
      )}

      {showCompany && (
        <span className="mt-3 flex items-center gap-2">
          <StandLogo url={job.stand.logoUrl} className="size-7" />
          <span className="text-sm font-semibold text-fog-500">{job.stand.title}</span>
        </span>
      )}
    </Link>
  );
}
